import { Box, Paper, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import { DayOfWeek, Lesson } from '../../types';
import { Conflict } from '../../types';
import ConflictBadge from './ConflictBadge';

interface TimetableGridProps {
  lessons: Lesson[];
  periods: number[];
  days?: DayOfWeek[];
  conflicts?: Conflict[];
  onLessonClick?: (lesson: Lesson) => void;
  showTeacher?: boolean;
  showRoom?: boolean;
  showStudentGroup?: boolean;
  colorBySubject?: boolean;
  highlightConflicts?: boolean;
  compact?: boolean;
  responsive?: boolean;
}

const defaultDays: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

const dayLabels: Record<DayOfWeek, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
};

const dayLabelsShort: Record<DayOfWeek, string> = {
  monday: 'M',
  tuesday: 'T',
  wednesday: 'W',
  thursday: 'Th',
  friday: 'F',
};

// Subject category colors for better visual grouping
const subjectCategoryColors: Record<string, string> = {
  'subject-math': '#e3f2fd',
  'subject-math-alevel': '#bbdefb',
  'subject-further-math': '#90caf9',
  'subject-english-lang': '#c8e6c9',
  'subject-english-lit': '#a5d6a7',
  'subject-english-alevel': '#81c784',
  'subject-biology': '#dcedc8',
  'subject-chemistry': '#ffe0b2',
  'subject-physics': '#ffecb3',
  'subject-science': '#fff9c4',
  'subject-history': '#f3e5f5',
  'subject-geography': '#e1bee7',
  'subject-pe': '#ffccbc',
  'subject-art': '#fce4ec',
  'subject-music': '#f8bbd9',
  'subject-drama': '#e1bee7',
  'subject-ict': '#b3e5fc',
  'subject-dt': '#ffcc80',
  'subject-re': '#d7ccc8',
  'subject-pshe': '#cfd8dc',
  'subject-mfl': '#b2dfdb',
};

// Generate consistent colors for subjects
const getSubjectColor = (subjectId: string): string => {
  // Check if we have a predefined color for this subject
  if (subjectCategoryColors[subjectId]) {
    return subjectCategoryColors[subjectId];
  }

  const colors = [
    '#e3f2fd', // blue
    '#f3e5f5', // purple
    '#e8f5e9', // green
    '#fff3e0', // orange
    '#fce4ec', // pink
    '#f1f8e9', // light green
    '#e0f2f1', // teal
    '#fff9c4', // yellow
    '#ede7f6', // deep purple
    '#e8eaf6', // indigo
  ];

  // Simple hash function to get consistent color for subject
  let hash = 0;
  for (let i = 0; i < subjectId.length; i++) {
    hash = subjectId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export const TimetableGrid: React.FC<TimetableGridProps> = ({
  lessons,
  periods,
  days = defaultDays,
  conflicts = [],
  onLessonClick,
  showTeacher = true,
  showRoom = true,
  showStudentGroup = true,
  colorBySubject = true,
  highlightConflicts = true,
  compact = false,
  responsive = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Adjust display settings based on screen size
  const displaySettings = useMemo(() => {
    if (!responsive) {
      return { showTeacher, showRoom, showStudentGroup, cellHeight: 80 };
    }
    if (isMobile) {
      return { showTeacher: false, showRoom: false, showStudentGroup: false, cellHeight: 60 };
    }
    if (isTablet || compact) {
      return { showTeacher: true, showRoom: false, showStudentGroup: false, cellHeight: 70 };
    }
    return { showTeacher, showRoom, showStudentGroup, cellHeight: 80 };
  }, [isMobile, isTablet, compact, responsive, showTeacher, showRoom, showStudentGroup]);

  // Build lesson lookup by day and period - support multiple lessons per slot
  const lessonMap = useMemo(() => {
    const map = new Map<string, Lesson[]>();
    lessons.forEach((lesson) => {
      const key = `${lesson.day}-${lesson.period}`;
      const existing = map.get(key) || [];
      existing.push(lesson);
      map.set(key, existing);
    });
    return map;
  }, [lessons]);

  // Build conflict lookup by lesson ID
  const conflictMap = useMemo(() => {
    const map = new Map<string, Conflict[]>();
    conflicts.forEach((conflict) => {
      conflict.affectedLessons.forEach((lessonId) => {
        const existing = map.get(lessonId) || [];
        existing.push(conflict);
        map.set(lessonId, existing);
      });
    });
    return map;
  }, [conflicts]);

  const getLessons = (day: DayOfWeek, period: number): Lesson[] => {
    return lessonMap.get(`${day}-${period}`) || [];
  };

  const getLessonConflicts = (lessonId: string): Conflict[] => {
    return conflictMap.get(lessonId) || [];
  };

  const renderLessonCell = (day: DayOfWeek, period: number) => {
    const cellLessons = getLessons(day, period);

    if (cellLessons.length === 0) {
      return (
        <Box
          sx={{
            height: '100%',
            minHeight: displaySettings.cellHeight,
            border: '1px solid',
            borderColor: 'grey.300',
            backgroundColor: 'grey.50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="caption" color="text.disabled">
            -
          </Typography>
        </Box>
      );
    }

    // Single lesson
    if (cellLessons.length === 1) {
      const lesson = cellLessons[0];
      const lessonConflicts = getLessonConflicts(lesson.id);
      const hasConflicts = lessonConflicts.length > 0;
      const backgroundColor = colorBySubject ? getSubjectColor(lesson.subjectId) : 'background.paper';
      const borderColor = hasConflicts && highlightConflicts ? 'error.main' : 'grey.300';
      const borderWidth = hasConflicts && highlightConflicts ? 2 : 1;

      return (
        <Tooltip
          title={
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {lesson.subjectName || lesson.subjectId}
              </Typography>
              <Typography variant="caption">Teacher: {lesson.teacherName || lesson.teacherId}</Typography>
              <Typography variant="caption" display="block">
                Room: {lesson.roomName || lesson.roomId}
              </Typography>
              <Typography variant="caption" display="block">
                Group: {lesson.groupName || lesson.studentGroupId}
              </Typography>
            </Box>
          }
          arrow
        >
          <Box
            onClick={() => onLessonClick?.(lesson)}
            sx={{
              height: '100%',
              minHeight: displaySettings.cellHeight,
              border: `${borderWidth}px solid`,
              borderColor,
              backgroundColor,
              p: compact || isMobile ? 0.5 : 1,
              cursor: onLessonClick ? 'pointer' : 'default',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              overflow: 'hidden',
              transition: 'all 0.2s',
              '&:hover': onLessonClick
                ? {
                    boxShadow: 2,
                    transform: 'scale(1.02)',
                    zIndex: 1,
                  }
                : {},
            }}
          >
            <Typography variant={compact || isMobile ? 'caption' : 'body2'} fontWeight="bold" noWrap>
              {lesson.subjectName || lesson.subjectId}
            </Typography>

            {displaySettings.showTeacher && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {lesson.teacherName || lesson.teacherId}
              </Typography>
            )}

            {displaySettings.showRoom && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {lesson.roomName || lesson.roomId}
              </Typography>
            )}

            {displaySettings.showStudentGroup && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {lesson.groupName || lesson.studentGroupId}
              </Typography>
            )}

            {hasConflicts && highlightConflicts && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {lessonConflicts.map((conflict) => (
                  <ConflictBadge key={conflict.id} conflict={conflict} size="small" showLabel={false} />
                ))}
              </Box>
            )}
          </Box>
        </Tooltip>
      );
    }

    // Multiple lessons in slot (stacked view)
    return (
      <Box
        sx={{
          height: '100%',
          minHeight: displaySettings.cellHeight,
          border: '1px solid',
          borderColor: 'warning.main',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          p: 0.25,
          overflow: 'auto',
        }}
      >
        {cellLessons.map((lesson) => {
          const lessonConflicts = getLessonConflicts(lesson.id);
          const hasConflicts = lessonConflicts.length > 0;
          const backgroundColor = colorBySubject ? getSubjectColor(lesson.subjectId) : 'background.paper';

          return (
            <Tooltip
              key={lesson.id}
              title={
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {lesson.subjectName || lesson.subjectId}
                  </Typography>
                  <Typography variant="caption">Teacher: {lesson.teacherName || lesson.teacherId}</Typography>
                  <Typography variant="caption" display="block">
                    Room: {lesson.roomName || lesson.roomId}
                  </Typography>
                </Box>
              }
              arrow
            >
              <Box
                onClick={() => onLessonClick?.(lesson)}
                sx={{
                  flex: 1,
                  minHeight: 24,
                  backgroundColor,
                  border: hasConflicts && highlightConflicts ? '2px solid' : '1px solid',
                  borderColor: hasConflicts && highlightConflicts ? 'error.main' : 'grey.300',
                  borderRadius: 0.5,
                  p: 0.25,
                  cursor: onLessonClick ? 'pointer' : 'default',
                  overflow: 'hidden',
                  '&:hover': onLessonClick ? { boxShadow: 1 } : {},
                }}
              >
                <Typography variant="caption" fontWeight="bold" noWrap>
                  {lesson.subjectName || lesson.subjectId}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    );
  };

  // Calculate grid column width based on screen size
  const periodLabelWidth = isMobile ? 60 : isTablet ? 80 : 100;

  return (
    <Paper sx={{ p: isMobile ? 1 : 2, overflow: 'auto' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `${periodLabelWidth}px repeat(${days.length}, 1fr)`,
          gridTemplateRows: `${isMobile ? 32 : 40}px repeat(${periods.length}, minmax(${displaySettings.cellHeight}px, auto))`,
          gap: 0,
          minWidth: isMobile ? 300 : 600,
        }}
      >
        {/* Top-left empty cell */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            backgroundColor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            Period
          </Typography>
        </Box>

        {/* Day headers */}
        {days.map((day) => (
          <Box
            key={day}
            sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant={isMobile ? 'caption' : 'body2'} fontWeight="bold">
              {isMobile ? dayLabelsShort[day] : dayLabels[day]}
            </Typography>
          </Box>
        ))}

        {/* Period rows */}
        {periods.map((period) => (
          <React.Fragment key={period}>
            {/* Period label */}
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'grey.300',
                backgroundColor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                {period}
              </Typography>
            </Box>

            {/* Lesson cells for this period */}
            {days.map((day) => (
              <Box key={`${day}-${period}`}>{renderLessonCell(day, period)}</Box>
            ))}
          </React.Fragment>
        ))}
      </Box>
    </Paper>
  );
};

export default TimetableGrid;
